<template>
  <b-modal 
    id="modal-duplicate-page" 
    :header-class="`align-items-center px-20 py-15 ${!isCanDup ? 'warning' : ''}`"
    body-class="p-20"
    footer-class="px-20 py-15"
    no-close-on-esc 
    no-close-on-backdrop
  >
    <template #modal-header>
      <div class="modal-title">
        <span class="fs-db-28">
          {{
            isCanDup === true
              ? $t("engine.managePage.pageDuplicate.lblTitle", languageEngine)
              : $t("engine.managePage.pageDuplicate.lblCantDup", languageEngine)
          }}
        </span>
      </div>
      <div
        id="close-modal-duplicate-page"
        type="button"
        class="close-modal"
        :class="{ 'text-muted': isSaving }"
        aria-label="Close"
        @click="isSaving ? false : close()"
      >
        <i class="fal fa-times"></i>
      </div>
    </template>

    <b-overlay variant="white" :show="isLoading || isSaving" :opacity="isSaving ? 0.5 : 1">
      <div v-if="isCanDup">
        <b-row>
          <b-col class="fs-sa-16">
            {{ $t("engine.managePage.pageDuplicate.lblDescription", languageEngine) }}
          </b-col>
        </b-row>

        <b-row class="mt-20">
          <b-col cols="4">
            <div class="fs-sa-14 mt-4">
              <span>{{ $t("engine.managePage.pageDuplicate.lblPage", languageEngine) }}: {{ pageName }}</span>
              <span class="text-hint ml-3 fs-sa-14">({{ languageUse && languageUse.toUpperCase() }})</span>
              <span class="float-right">
                <i class="fa fa-chevron-right"></i>
              </span>
            </div>
          </b-col>
          <b-col cols="8">
            <b-form-radio
              v-for="language in languages"
              class="fs-sa-14 mb-10"
              style="line-height: 25px;"
              :style="{
                opacity: languagesDisabled.includes(language.language) ? '0.3' : '1'
              }"
              :key="`${language.language}`"
              :value="`${language.language}`"
              v-model="lang"
              :disabled="languagesDisabled.includes(language.language)"
            >
              <span><i :class="`flag-icon flag-icon-${language.flagIcon}`"></i></span>
              <span class="ml-5">{{ language.language.toUpperCase() }}</span>
              <span class="fs-sa-14 text-hint ml-3">
                <template v-if="language.language === languageUse && languagesDisabled.includes(language.language)">
                  ({{ $t("engine.managePage.pageDuplicate.lblLanguageMain", languageEngine) }}
                  -
                  {{ $t("engine.managePage.pageManage.lblPageUsed", languageEngine) }})
                </template>
                <template v-else-if="languagesDisabled.includes(language.language)">
                  ({{ $t("engine.managePage.pageManage.lblPageUsed", languageEngine) }})
                </template>
                <template v-else-if="language.language === languageUse">
                  ({{ $t("engine.managePage.pageDuplicate.lblLanguageMain", languageEngine) }})
                </template>
              </span>
            </b-form-radio>
          </b-col>
        </b-row>

        <b-row class="mt-20">
          <b-col sm="4">
            <label class="fs-sa-14 my-8" for="pageName">
              <span>{{  $t("engine.managePage.pageDuplicate.lblPageDupName", languageEngine) }}</span>
              <span class="text-hint ml-3" v-if="lang">({{ lang.toUpperCase() }})</span>
              <span class="text-danger ml-3">*</span>
            </label>
          </b-col>
          <b-col cols="8" :class="{ errors: $v.name.$error }">
            <b-form-input
              id="pageName"
              v-model="$v.name.$model"
              :placeholder='$t("engine.managePage.pageDuplicate.plcPageDupName", languageEngine)'
            />
            <small data-validate="required" v-if="!$v.name.required && $v.name.$error">{{ $t("engine.managePage.pageDuplicate.errPageNameEmpty", languageEngine) }}</small>
            <small data-validate="maxLength" v-if="!$v.name.maxLength && $v.name.$error">{{ $t("engine.managePage.pageDuplicate.errPageNameLength", languageEngine) }}</small>
            <!-- <small data-validate="alpha" v-if="!$v.name.alpha && $v.name.$error">{{ $t("engine.managePage.pageDuplicate.errPageNameFormat", languageEngine) }}</small> -->
          </b-col>
        </b-row>

        <b-row class="mt-20">
          <b-col sm="4">
            <label class="fs-sa-14 my-8" for="page-url">
              <span>{{ $t("engine.managePage.pageDuplicate.lblPageDupURL", languageEngine) }}</span>
              <span class="text-hint ml-3" v-if="lang">({{ lang.toUpperCase() }})</span>
              <span class="text-danger ml-3">*</span>
            </label>
          </b-col>
          <b-col cols="8" :class="{ errors: $v.url.$error }">
            <b-input-group
              class="mr-sm-2"
              :class="{
                'has-validation': urlStatus == 'checking',
              }"
              :prepend="`${(lang && lang.toLowerCase())}/`"
            >
              <b-form-input
                v-if="pageType == 'section'"
                id="page-url"
                :value="url"
                @paste="handlePasteUrl"
                @input="handleInputUrl"
                @change="handleChangeUrl"
              />
              <b-form-input
                v-else
                id="page-url"
                :value="urlFull"
                :readonly="true"
              />
              <span class="icon-check-url" v-if="urlStatus == 'checking'">
                <i class="far fa-spinner fa-spin"></i>
              </span>
            </b-input-group>
            <small data-validate="required" v-if="!$v.url.required && $v.url.$error">{{ $t('engine.managePage.pageDuplicate.errUrlEmpty', languageEngine) }}</small>
            <small data-validate="maxLength" v-else-if="!$v.url.maxLength && $v.url.$error">{{ $t('engine.managePage.pageDuplicate.errUrlLength', languageEngine) }}</small>
            <small data-validate="alphaUrl" v-else-if="!$v.url.alphaUrl && $v.url.$error">{{ $t('engine.managePage.pageDuplicate.errUrlFormat', languageEngine) }}</small>
            <small data-validate="checkUrlChanged" v-else-if="!$v.url.checkUrlChanged && $v.url.$error">{{ $t('engine.managePage.pageDuplicate.errUrlCheckChanged', languageEngine) }}</small>
            <small data-validate="reserveUrl" v-else-if="!$v.url.reserveUrl && $v.url.$error">{{ $t('engine.managePage.pageDuplicate.errUrlReserve', languageEngine) }}</small>
            <small data-validate="isUnique" v-else-if="!$v.url.isUnique && $v.url.$error">{{ $t('engine.managePage.pageDuplicate.errUrlExists', languageEngine) }}</small>
          </b-col>
        </b-row>

        <b-row class="mt-20">
          <b-col sm="4">
            <label class="fs-sa-14" for="checkbox-create-menu">
              <span>{{ $t("engine.managePage.pageDuplicate.lblPageDupMenu", languageEngine) }}</span>
            </label>
          </b-col>
          <b-col cols="8">
            <b-form-checkbox
              id="checkbox-create-menu"
              name="checkbox-create-menu"
              v-model="createMenu"
              :value="true"
              :unchecked-value="false"
            />
          </b-col>
        </b-row>
      </div>
      <div v-else>
        <b-row>
          <b-col class="fs-sa-16">
            {{  $t("engine.managePage.pageDuplicate.lblCantDupDescription", languageEngine) }}
          </b-col>
        </b-row>
      </div>
      <template #overlay>
        <Loading :title="isSaving ? false : true" />
      </template>
    </b-overlay>

    <template #modal-footer>
      <template v-if="isCanDup">
        <b-button variant="blue" @click.prevent="save()" :disabled="$v.$error || isLoading || isSaving || urlStatus == 'checking'">
          <i class="far fa-check"></i>
          {{ $t("engine.managePage.pageDuplicate.btnSave", languageEngine) }}
        </b-button>
        <b-button variant="default" @click="close()" :disabled="isLoading || isSaving">
          <i class="far fa-times "></i>
          {{ $t("engine.managePage.pageDuplicate.btnCancel", languageEngine) }}
        </b-button>
      </template>
      <template v-else>
        <b-button variant="blue" @click="close()" :disabled="isLoading || isSaving">
          <i class="far fa-check "></i>
          {{ $t("engine.managePage.pageDuplicate.btnOk", languageEngine) }}
        </b-button>
      </template>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex";
import _ from 'lodash';
import Loading from "@components_system/Shared/Tools/Loading/Loading";
import siteService from "@services/siteService.js";
import pageService from "@services/pageService.js";
import { PAGE, HEADER_MENU } from "@model/components/componentMeta.model";
import { required, maxLength } from "vuelidate/lib/validators";
import { genUniqKey } from "@helpers/component.helper";
import { getPageUrlSystem } from "@model/masterData";

import albumService from "@services/albumService.js";
import contentService from "@services/contentService.js";
import productManagerService from "@services/productManagerService.js"
import formManagerService from "@services/formManagerService.js";

export default {
  components: {
    Loading,
  },
  props: {
    id: {
      type: String,
      default: "",
    },
    action: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      isLoading: true,
      isSaving: false,
      isCanDup: true,
      pageData: null,
      pageContent: null,
      pageResource: {},
      lang: null,
      name: "",
      url: "", // product/123
      urlFull: "", // product/123/iphone
      createMenu: true,
      isUrlDefault: true,
      urlStatus: 'none',
      languagesDisabled: [],
      pagesUsed: {
        system : [],
        item: [],
      },
      pagesUsedTypes : {
        system : ['faq', 'contact', 'online_payment', 'inform_payment', 'terms_condition', 'product_brand_list', 'order_tracking','search'],
        item: ['content_list', 'content_item', 'album_list', 'album_item', 'form_item', 'product_list', 'product_item']
      },
      validationConfigs: {
        url: {
          required: { enable: true },
          maxLength: { enable: true },
          alphaUrl: { enable: true },
          isUnique: { enable: true },
        }
      }
    };
  },
  computed: {
    ...mapGetters({
      languages: "design/site/languages",
      languageDefault: "design/site/languageDefault",
      languageUse: "design/site/languageUse",
      languageEngine: "design/userPreference/languageEngine",
      siteComponents: "design/metadata/siteComponents",
      design: "design/site/design",
    }),
    reserveUrls() {
      return getPageUrlSystem();
    },
    pageType() {
      return (this.pageData && this.pageData.properties.type) || undefined;
    },
    pageName() {
      return (this.pageData && this.pageData.properties.name) || '';
    },
    pageUrl() {
      return (this.pageData && this.pageData.properties.url) || '';
    },
    pageMetaDataID() {
      return (this.pageData && this.pageData.properties.metaDataID) || undefined;
    },
    pageIsSystem() {
      return (this.pageData && this.pageData.properties.groupType == 'system') || false;
    },
    sectionPageData() {
      return (this.pageContent && this.pageContent.assets.components.SECTION_PAGE && this.pageContent.assets.components.SECTION_PAGE.data) || {};
    },
  },
  methods: {
    genUniqKey,
    async init() {
      this.isLoading = true;
      this.isCanDup = true;
      this.isSaving = false;

      this.lang = this.languageUse;
      this.pageData = this.siteComponents[this.id]; // SITE > PAGE
      this.name = this.genNameDefault(this.pageName);
      this.languagesDisable = [];
      await this.$store.dispatch("design/snapshot/synceSnapshot" , { });
      if (this.pageType === 'section') {
        const res = await Promise.all([
          this.fetchPageContent(this.pageMetaDataID),
          this.genUrlCustom(this.pageUrl),
        ]);
        this.pageContent = res[0]; // PAGE
        this.url = res[1];
        this.urlFull = res[1];
        this.isUrlDefault = true;
        this.validationConfigs.url.isUnique.enable = false;

        this.$v.$reset();
        this.isLoading = false;

      } else {
        const res = await Promise.all([
          this.fetchPageContent(this.pageMetaDataID),
          this.fetchPagesUsed(this.pageMetaDataID),
        ]);
        this.pageContent = res[0]; // PAGE
        this.pagesUsed = res[1];

        // Languages Disabled
        this.languagesDisabled = Object.values(this.languages).filter(item => this.checkLanguageDisabled(item.language)).map(item => item.language);
        
        // Change lang (disabled)
        if (this.languagesDisabled.includes(this.lang)) {
          this.lang = Object.keys(this.languages).find(lang => !this.languagesDisabled.includes(lang));
        }

        // No lang (all disabled)
        if (!this.lang) {
          this.$v.$reset();
          this.isLoading = false;
          this.isCanDup = false;
          return;
        }

        // Url
        const genUrl = await this.genUrlSystem(this.pageResource);
        this.url = genUrl.url;
        this.urlFull = genUrl.urlFull;
        this.isUrlDefault = true;
        this.validationConfigs.url.isUnique.enable = false;

        this.$v.$reset();
        this.isLoading = false;
      }
    },
    clear() {
      this.pageData = null;
      this.pageContent = null;
      this.pageResource = {};
      this.lang = null;
      this.name = '';
      this.url = '';
      this.urlFull = '';
      this.createMenu = true,
      this.isUrlDefault = true;
      this.validationConfigs.url.isUnique.enable = false;
      this.urlStatus = 'none';
      this.languagesDisabled = [];
      this.pagesUsed = {
        system : [],
        item: [],
      };
      this.$v.$reset();
    },


    // Page Used
    async fetchPagesUsed(metaDataID) {
      const pagesInfo = await pageService.getPagesUsed({ filterType : 'content', pageID : metaDataID });
      return {
        system: Object.values(pagesInfo)[1].filter((ele) => this.pagesUsedTypes.system.includes(ele.type)),
        item: Object.values(pagesInfo)[1].filter((ele) => this.pagesUsedTypes.item.includes(ele.type))
      }
    },
    checkLanguageDisabled(language) {
      const pagesUsedTypes = this.pagesUsedTypes;
      const pagesUsed = this.pagesUsed;
      const pageType = this.pageType;
      const sectionPageData = this.sectionPageData;
      
      if (pagesUsedTypes.system.includes(pageType)) {
        return Object.values(pagesUsed.system).some(page => Boolean(page.language === language));

      } else if (pagesUsedTypes.item.includes(pageType)) {
        return Object.values(pagesUsed.item).some(page => {
          if (page.language === language) { 
            switch(pageType) {                
              case "album_list":
                if (this.$helper.data.idNotNull(page.categoryID) === this.$helper.data.idNotNull(sectionPageData.categoryID)) return true;
                break;
              case "album_item":
                if (page.itemID === sectionPageData.albumID) return true;
                break;
              case "content_list":
                if (_.isEqual(this.$helper.data.objectIDNotNull(page.categoryID, 3), this.$helper.data.objectIDNotNull(sectionPageData.categoryID, 3))) return true;
                break;
              case "content_item":
                if (page.itemID === sectionPageData.contentID) return true;
                break;
              case "product_list": {
                if (page.dataType == sectionPageData.type && page.dataType == 'category' && _.isEqual(this.$helper.data.objectIDNotNull(page.categoryID, 4), this.$helper.data.objectIDNotNull(sectionPageData.categoryID, 4))) return true;
                if (page.dataType == sectionPageData.type && page.dataType == 'best_seller' && _.isEqual(this.$helper.data.objectIDNotNull(page.categoryID, 4), this.$helper.data.objectIDNotNull(sectionPageData.categoryID, 4))) return true;
                if (page.dataType == sectionPageData.type && page.dataType == 'brand' && this.$helper.data.idNotNull(page.brandID) === this.$helper.data.idNotNull(sectionPageData.brandID)) return true;
              } break;
              case "product_item":
                if (page.itemID === sectionPageData.productID) return true;
                break;
              case "form_item":
                if (page.itemID === sectionPageData.formID) return true;
                break;
              default:
                break;
            }
          }
        });
      }
    },


    // Page
    async fetchPageContent(metaDataID) {
      const response = await siteService.getSitePage({ lang: this.languageUse, id: metaDataID }); // PAGE
      return response;
    },
    genNameDefault(name) {
      return name + " - Copy";
    },
    async genUrlCustom(url) {
      const response = await pageService.checkUrlDuplicate({ language: this.lang, url: url });
      return (response.isAvailable === false) ? response.suggest : url;
    },
    async genUrlSystem(pageResource) {
      const language = this.lang;
      const pageType = this.pageType;
      const sectionPageData = this.sectionPageData;

      if (pageResource[language] === undefined) this.pageResource[language] = await this.fetchPageResource(language, this.sectionPageData);

      switch(pageType) {  
        case "album_list": return this.$helper.url.genUrl(pageType, { category: pageResource[language].category });
        case "album_item": return this.$helper.url.genUrl(pageType, { album: pageResource[language].album });
        case "content_list": return this.$helper.url.genUrl(pageType, { category: pageResource[language].category });
        case "content_item": return this.$helper.url.genUrl(pageType, { content: pageResource[language].content });
        case "product_list": {
          const dataType = sectionPageData.type;
          if (dataType == 'category') return this.$helper.url.genUrl(pageType, { dataType, category: pageResource[language].category });
          if (dataType == 'best_seller') return this.$helper.url.genUrl(pageType, { dataType, category: pageResource[language].category });
          if (dataType == 'brand') return this.$helper.url.genUrl(pageType, { dataType, brand: pageResource[language].brand });
        } break;
        case "product_item": return this.$helper.url.genUrl(pageType, { product: pageResource[language].product });
        case "form_item": return this.$helper.url.genUrl(pageType, { form: pageResource[language].form });
        default:
          break;
      }
      return {
        url: this.pageUrl,
        urlFull: this.pageUrl + `/{name_${this.lang}}`,
      }
    },


    // Resource
    async fetchPageResource(language, sectionPageData) {
      const pageType = this.pageType;

      switch(pageType) {                
        case "album_list": {
          const categoryID = this.$helper.data.idNotNull(sectionPageData.categoryID);
          const categories = await this.fetchAlbumCategories(language);
          const category = this.findAlbumCategory(categories, categoryID);
          return {
            categoryID: categoryID,
            categories: categories,
            category: category,
          }
        }
        case "album_item": {
          const categoryID = this.$helper.data.idNotNull(sectionPageData.categoryID);
          const albumID = sectionPageData.albumID;
          const albums = await this.fetchAlbums(language, categoryID);
          const album = this.findAlbum(albums, albumID);
          return {
            categoryID: categoryID,
            albumID: albumID,
            albums: albums,
            album: album,
          }
        }
        case "content_list": {
          const categoryID = this.$helper.data.objectIDNotNull(sectionPageData.categoryID, 3);
          const categories = await this.fetchContentCategories(language);
          const category = this.findContentCategory(categories, categoryID);
          return {
            categoryID: categoryID,
            categories: categories,
            category: category,
          }
        }
        case "content_item": {
          const categoryID = this.$helper.data.objectIDNotNull(sectionPageData.categoryID, 3);
          const contentID = sectionPageData.contentID;
          const contents = await this.fetchContents(language, categoryID);
          const content = this.findContent(contents, contentID);
          return {
            categoryID: categoryID,
            contentID: contentID,
            contents: contents,
            content: content,
          }
        }
        case "product_list": {
          if (sectionPageData.type == 'category') {
            const categoryID = this.$helper.data.objectIDNotNull(sectionPageData.categoryID, 4);
            const categories = await this.fetchProductCategories(language);
            const category = this.findProductCategory(categories, categoryID);
            return {
              categoryID: categoryID,
              categories: categories,
              category: category,
            }
          }
          if (sectionPageData.type == 'best_seller') {
            const categoryID = this.$helper.data.objectIDNotNull(sectionPageData.categoryID, 4);
            const categories = await this.fetchProductCategories(language);
            const category = this.findProductCategory(categories, sectionPageData.categoryID);
            return {
              categoryID: categoryID,
              categories: categories,
              category: category,
            }
          }
          if (sectionPageData.type == 'brand') {
            const brandID = this.$helper.data.idNotNull(sectionPageData.brandID);
            const brands = await this.fetchProductBrands(language);
            const brand = this.findProductBrand(brands, brandID);
            return {
              brandID: brandID,
              brands: brands,
              brand: brand,
            }
          }
          return {};
        }
        case "product_item": {
          const categoryID = this.$helper.data.objectIDNotNull(sectionPageData.categoryID, 4);
          const productID = sectionPageData.productID;
          const products = await this.fetchProducts(language, categoryID);
          const product = this.findProduct(products, productID);
          return {
            categoryID: categoryID,
            productID: productID,
            products: products,
            product: product,
          }
        }
        case "form_item": {
          const forms = await this.fetchForms(language);
          const form = this.findForm(forms, sectionPageData.formID);
          return {
            forms: forms,
            form: form,
          }
        }
        default:
          break;
      }
      return {};
    },
    // Resource: Album
    async fetchAlbumCategories(language) {
      if (language) {
        const response = await albumService.getAlbumCategories(language);
        return response || [];
      }
      return [];
    },
    findAlbumCategory(categories, categoryID) {
      let category = {};
      if (categoryID == '0') return { id: '0' } // all
      if (categoryID) {
        category = categories.find((category) => category.id == categoryID);
      }
      return category;
    },
    async fetchAlbums(language, categoryID) {
      if (language && categoryID) {
        if (categoryID != '0' && categoryID !== undefined && categoryID !== null) { // must have category
          const response = await albumService.getAlbumListAll({
            category: categoryID,
            language: language,
          });
          return response.data || [];
        }
      }
      return [];
    },
    findAlbum(albums, albumID) {
      let album = {};
      if (albumID == '0') return { id: '0' } // all
      if (albumID) {
        album = albums.find((album) => album.id == albumID);
      }
      return album;
    },
    // Resource: Content
    async fetchContentCategories(language) {
      if (language) {
        const response = await contentService.getCategories(language);
        return response.data.data || [];
      }
      return [];
    },
    findContentCategory(categories, categoryID) {
      let categoryLevel1 = null;
      let categoryLevel2 = null;
      let categoryLevel3 = null;

      if (categoryID && categoryID.level1 == '0') {
        return {
          level1: { id: '0' },
          level2: null,
          level3: null,
        }
      }

      if (categoryID) {
        // Level1
        if (categories && categoryID.level1) {
          categories.find((cate1) => {
            if (cate1.id == categoryID.level1) {
              categoryLevel1 = JSON.parse(JSON.stringify(cate1));
              delete categoryLevel1["children"];
              delete categoryLevel1["childrenCount"];

              // Level2
              if (cate1.children && categoryID.level2) {
                cate1.children.find((cate2) => {
                  if (cate2.id == categoryID.level2) {
                    categoryLevel2 = JSON.parse(JSON.stringify(cate2));
                    delete categoryLevel2["children"];
                    delete categoryLevel2["childrenCount"];

                    // Level3
                    if (cate2.children && categoryID.level3) {
                      cate2.children.find((cate3) => {
                        if (cate3.id == categoryID.level3) {
                          categoryLevel3 = JSON.parse(JSON.stringify(cate3));
                          delete categoryLevel3["children"];
                          delete categoryLevel3["childrenCount"];
                          return true;
                        }
                      });
                    }
                    return true;
                  }
                });
              }
              return true;
            }
          });
        }
      }
      return {
        level1: categoryLevel1,
        level2: categoryLevel2,
        level3: categoryLevel3,
      };
    },
    async fetchContents(language, categoryID) {
      if (language) {
        if (categoryID != '0' && categoryID !== undefined && categoryID !== null && categoryID.level1 !== null) { // must have category
          const response = await contentService.getContentByCate({
            language: language,
            type: "category",
            sortType: "recenly",
            start: 1,
            contentDisplay: 20,
            category1: categoryID.level1 || 0,
            category2: categoryID.level2 || 0,
            category3: categoryID.level3 || 0,
          });
          return response.data.map((data) => {
            return { 
              id: data.id, 
              name: data.content_name,
              isEnable: (data.isEnable === undefined) ? true : data.isEnable,
            };
          });
        }
      }
      return [];
    },
    findContent(contents, contentID) {
      let content = {};
      if (contentID == '0') return { id: '0' } // all
      if (contentID) {
        content = contents.find((content) => content.id == contentID);
      }
      return content;
    },
    // Resource: Product
    async fetchProductCategories(language) {
      if (language) {
        const response = await productManagerService.getCategories(language);
        return response.data;
      }
      return [];
    },
    findProductCategory(categories, categoryID) {
      let categoryLevel1 = null; 
      let categoryLevel2 = null; 
      let categoryLevel3 = null;
      let categoryLevel4 = null;

      if (categoryID && categoryID.level1 == '0') {
        return {
          level1: { id: '0' },
          level2: null,
          level3: null,
          level4: null,
        }
      }

      if (categoryID) {
        // Level1
        if(categories && categoryID.level1) {
          categories.find(cate1 => {
            if (cate1.id == categoryID.level1) {
              categoryLevel1 = JSON.parse(JSON.stringify(cate1));
              delete categoryLevel1['children']; 
              delete categoryLevel1['childrenCount'];
              
              // Level2
              if (cate1.children && categoryID.level2) {
                cate1.children.find(cate2 => {
                  if (cate2.id == categoryID.level2) {
                    categoryLevel2 = JSON.parse(JSON.stringify(cate2));
                    delete categoryLevel2['children']; 
                    delete categoryLevel2['childrenCount'];
                    
                    // Level3
                    if (cate2.children && categoryID.level3) {
                      cate2.children.find(cate3 => {
                        if (cate3.id == categoryID.level3) {
                          categoryLevel3 = JSON.parse(JSON.stringify(cate3));
                          delete categoryLevel3['children']; 
                          delete categoryLevel3['childrenCount'];
                          
                          // Level4
                          if (cate3.children && categoryID.level4) {
                            cate3.children.find(cate4 => {
                              if (cate4.id == categoryID.level4) {
                                categoryLevel4 = JSON.parse(JSON.stringify(cate4));
                                delete categoryLevel3['children']; 
                                delete categoryLevel3['childrenCount'];

                                return true;
                              }
                            });
                          }
                          return true;
                        }
                      });
                    }
                    return true;
                  }
                });
              }
              return true;
            }
          });
        }
      }

      return {
        level1: categoryLevel1,
        level2: categoryLevel2,
        level3: categoryLevel3,
        level4: categoryLevel4,
      }
    },
    async fetchProductBrands(language) {
      if (language) {
        const response = await productManagerService.getBrands(language);
        return response.data;
      }
      return [];
    },
    findProductBrand(brands, brandID) {
      let brand = null; 
      if (brandID == '0') return { id: '0' } // all
      if (brandID) {
        brand = brands.find(item => item.id == brandID);
      }
      return brand;
    },
    async fetchProducts(language, categoryID) {
      if (language) {
        if (categoryID != '0' && categoryID !== undefined && categoryID !== null && categoryID.level1 !== null) { // must have category
          const response = await productManagerService.getProducts({
            language: language,
            categoryLv1: categoryID.level1 || 0,
            categoryLv2: categoryID.level2 || 0,
            categoryLv3: categoryID.level3 || 0,
            categoryLv4: categoryID.level4 || 0,
          });
          return response.data || [];
        }
      }
      return [];
    },
    findProduct(products, productID) {
      let product = {};
      if (productID == '0') return { id: '0' } // all
      if (productID) {
        product = products.find(product => product.id == productID);
      }
      return product;
    },
    // Resource: Form
    async fetchForms(language) {
      const response = await formManagerService.getForms({ language: language });
      return response.result || [];
    },
    findForm(forms, formID) {
      let form = {};
      if (formID == '0') return { id: '0' } // all
      if (formID) {
        form = forms.find(form => form.id == formID);
      }
      return form;
    },


    // Normalize
    normalizeUrlCustom(value) {
      return this.$helper.url.urlCustomerCase(value);
    },
    albumCategoryIDNotNull(categoryID) {
      if (categoryID === null) return "0"; // empty => all
      return categoryID;
    },
    contentCategoryIDNotNull(categoryID) {
      if (!categoryID) return { level1: "0", level2: null, level3: null }; // empty => all
      if (categoryID.level1 === null) return { level1: "0", level2: null, level3: null }; // empty => all
      return { level1: categoryID.level1, level2: categoryID.level2, level3: categoryID.level3 };
    },
    productCategoryIDNotNull(categoryID) {
      if (!categoryID) return { level1: "0", level2: null, level3: null, level4: null }; // empty => all
      if (categoryID.level1 === null) return { level1: "0", level2: null, level3: null, level4: null }; // empty => all
      return { level1: categoryID.level1, level2: categoryID.level2, level3: categoryID.level3, level4: categoryID.level4 };
    },
    productBrandIDNotNull(brandID) {
      if (brandID === null) return "0"; // empty => all
      return brandID;
    },

    // Event Url
    handlePasteUrl(e) {
      e.preventDefault();
      const value = this.normalizeUrlCustom(e.clipboardData.getData('text'));
      this.$v.url.$model = value;
      this.validationConfigs.url.isUnique.enable = true;
    },
    handleInputUrl(value) {
      this.validationConfigs.url.isUnique.enable = false;
      this.$v.url.$model = value;
    },
    handleChangeUrl() {
      this.isUrlDefault = false;
      this.validationConfigs.url.isUnique.enable = true;
    },


    // Save, Cancel
    close() {
      this.$bvModal.hide("modal-duplicate-page");
      this.$emit("clear");
    },
    async save() {
      this.isSaving = true;
      // await this.$store.dispatch("design/snapshot/synceSnapshot" , { });
      // Validate
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.isSaving = false;
        return;
      }

      const name = this.name;
      const url = this.url;
      const createMenu = this.createMenu;
      let data = [];

      // Page
      let id_new = '{PAGE_ID_1}';
      if(this.lang == this.languageUse) {
        id_new = genUniqKey(this.siteComponents, PAGE.prefixKey);
      }
      data.push(
        {
          op: "add",
          path: `/languages/${this.lang}/SITE/assets/components/${id_new}`,
          value: {
            ...this.siteComponents[this.id],
            id: id_new,
            properties: {
              ...this.siteComponents[this.id].properties,
              name: name,
              url: url,
            },
            action : 'duplicate',
          },
        },
        {
          op: "add",
          path: `/languages/${this.lang}/pages/${id_new}`,
          value: {
            props: {
              type: "PAGE",
              id: id_new,
              properties: {
                type: this.pageContent.props.properties.type,
                groupType: this.pageContent.props.properties.groupType,
              },
              components: {
                CONTENT_CONTAINER: {
                  type: "CONTENT_CONTAINER",
                  componentsID: [
                    ...this.pageContent.props.components.CONTENT_CONTAINER
                      .componentsID,
                  ],
                },
              },
            },
            assets: {
              components: { ...this.pageContent.assets.components },
            },
          },
        }
      );

      // Menu
      let menuID = '{HEADER_MENU_ID_1}';
      let menuComponentTemp = {
        type: "HEADER_MENU",
        id: menuID,
        properties: {
          name: this.name,
          isEnable: true,
          link: {
            type: "page",
            urlIsEnable: false,
            url: null,
            pageID: id_new,
            sectionPageID: null,
            sectionID: null,
            target: "_self",
          },
        },
        componentsID: [],
      };
      if (createMenu) {
        if (this.lang != this.languageUse) {
          data.push(
            {
              op: "add",
              path: `/languages/${this.lang}/SITE/assets/components/HEADER_WIDGET_MENU/componentsID/{INDEX_LAST}`,
              value: `${menuID}`,
            },
            {
              op: "add",
              path: `/languages/${this.lang}/SITE/assets/components/${menuID}`,
              value : { ...menuComponentTemp }
            }
          );
        } else {
          menuID = genUniqKey(this.siteComponents, HEADER_MENU.prefixKey);
          const lastIndex = this.siteComponents.HEADER_WIDGET_MENU.componentsID.length;
          data.push(
            {
              op: "add",
              path : `/languages/${this.languageUse}/SITE/assets/components/HEADER_WIDGET_MENU/componentsID/${lastIndex}`,
              value : `${menuID}`
            },
            {
              op: "add",
              path: `/languages/${this.languageUse}/SITE/assets/components/${menuID}`,
              value: { ...menuComponentTemp, id :menuID },
            },
          )
        }
      }
      

      if (this.lang == this.languageUse) {
        this.createPage(data, id_new);
      } else {
        this.createPageManual(data)
      }
    },
    createPage(temp, id_new) {
      pageService.managesPage({ data: { actions: temp, transactionID: localStorage.getItem('lastTransactionID') } }).then(async (response) => {
        localStorage.setItem('lastTransactionID', response.transactionID)
        const metaDataId = response.result.pagesMetaDataID[this.lang][id_new];
        
        // Load Page
        await this.$store.dispatch("designIframe/page/getPage", { metaDataID: metaDataId });

        this.isSaving = false;
        this.$toast.success({ title: this.$t("engine.common.alert.success.title", this.languageEngine), message: this.$t("engine.managePage.pageDuplicate.msgDuplicateSuccess",this.languageEngine) });
        this.$emit("setMetaDataIDDuplicate", metaDataId); // for switch page
        this.$emit("setCopyName", this.name); // for switch page
        this.$emit("setCopyLang", this.lang); // for switch page
        this.$emit("scrollToPage", id_new); // pge0001
        this.close();
      }).catch(() => {
        this.isSaving = false;
        this.$toast.error({ title: this.$t("engine.common.alert.error.title", this.languageEngine), message: this.$t("engine.managePage.pageDuplicate.msgDuplicateFail", this.languageEngine) });
      });
    },
    createPageManual(temp) {
      const replaces = {
        "{PAGE_ID_1}": {
            "command": "generate_id",
            "language": "en",
            "componentType": "PAGE"
        },
        "{HEADER_MENU_ID_1}": {
            "command": "generate_id",
            "language": "en",
            "componentType": "HEADER_MENU"
        },
        "{INDEX_LAST}": {
            "command": "index_last",
            "path": "/languages/en/SITE/assets/components/HEADER_WIDGET_MENU/componentsID"
        }
      };
      pageService.managesPage({ data: { actions: temp, replaces, transactionID: localStorage.getItem('lastTransactionID') }}).then( async (response) => {
        localStorage.setItem('lastTransactionID', response.transactionID)
        const page_id =  Object.keys(response.result.pagesMetaDataID[this.lang])[0];
        const metaDataId = response.result.pagesMetaDataID[this.lang][page_id]; 
        
        this.$emit("setMetaDataIDDuplicate", metaDataId); // for switch page
        this.$emit("setCopyName", this.name); // for switch page
        this.$emit("setCopyLang", this.lang); // for switch page

        this.isSaving = false;
        this.$toast.success({ title: this.$t("engine.common.alert.success.title", this.languageEngine), message: this.$t("engine.managePage.pageDuplicate.msgDuplicateSuccess", this.languageEngine) });
        this.$bvModal.show("modal-confirm-switch-page");
        this.close();
      }).catch(() => {
        this.isSaving = false;
        this.$toast.error({ title: this.$t("engine.common.alert.error.title", this.languageEngine), message: this.$t("engine.managePage.pageDuplicate.msgDuplicateFail", this.languageEngine) });
      });
    },
  },
  watch: {
    action(valueNew, valueOld) {
      if (valueNew == "duplicate") {
        this.init();
      } else if (valueNew == '' && valueOld == 'duplicate') {
        this.clear();
      }
    },
    async lang() {
      if (this.action == "duplicate" && this.isLoading === false && this.isUrlDefault === true) { // re genUrl
        if (this.pageType === 'section') {
          this.urlStatus = "checking";
          this.validationConfigs.url.isUnique.enable = false;
          this.url = this.pageUrl;
          this.urlFull = this.pageUrl;
          const genUrl = await this.genUrlCustom(this.pageUrl);
          this.url = genUrl;
          this.urlFull = genUrl;
          this.urlStatus = "none";
        } else {
          this.urlStatus = "checking";
          this.validationConfigs.url.isUnique.enable = false;
          const genUrl = await this.genUrlSystem(this.pageResource);
          this.url = genUrl.url;
          this.urlFull = genUrl.urlFull;
          this.urlStatus = "none";
        }
      }
    },
  },
  validations: {
    lang: {
      required,
    },
    name: {
      required,
      maxLength: maxLength(50)
    },
    url: {
      required(value) {
        if (this.validationConfigs.url.required.enable === false) return true;
        if (this.pageType !== "section") return true; // not url custom
        
        if (value) {
          return true;
        }
        return false;
      },
      maxLength(value) {
        if (this.validationConfigs.url.maxLength.enable === false) return true;
        if (this.pageType !== "section") return true; // not url custom
        if (!this.$v.url.required) return true;

        if (value.toLowerCase().length <= 100) {
          return true;
        }
        return false;
      },
      alphaUrl(value) {
        if (this.validationConfigs.url.alphaUrl.enable === false) return true;
        if (this.pageType !== "section") return true; // not url custom
        if (!this.$v.url.required) return true;
        if (!this.$v.url.maxLength) return true;

        const temp = this.$helper.url.urlCustomerCase(value);
        if (value === temp) {
          return true;
        }
        return false;
      },
      checkUrlChanged(value) {
        if (this.pageType !== "section") return true; // not url custom
        if (!this.$v.url.required) return true;
        if (!this.$v.url.maxLength) return true;
        if (!this.$v.url.alphaUrl) return true;
        if (this.isSuggestUrl === false && value.startsWith('-')) {
          return false;
        }
        return true;
      },
      reserveUrl(value) {
        if (this.pageType !== "section") return true; // not url custom
        if (!this.$v.url.required) return true;
        if (!this.$v.url.maxLength) return true;
        if (!this.$v.url.alphaUrl) return true;
        if (!this.$v.url.checkUrlChanged) return true;
        
        if (this.reserveUrls.includes(value)) return false;
        return true;
      },
      async isUnique(value) {
        if (this.validationConfigs.url.isUnique.enable === false) return true;
        if (this.pageType !== "section") return true; // not url custom
        if (!this.$v.url.required) return true;
        if (!this.$v.url.maxLength) return true;
        if (!this.$v.url.alphaUrl) return true;
        if (!this.$v.url.checkUrlChanged) return true;
        if (!this.$v.url.reserveUrl) return true;

        this.urlStatus = "checking";
        const url = value;
        const language = this.lang;
        const response = await pageService.checkUrlDuplicate({ language, url });
        this.urlStatus = "none";
        
        return response.isAvailable;
      },
    },
  },
};
</script>

<style scoped>
.icon-check-url {
  position: absolute;
  right: 15px;
  top: 4px;
}
</style>